import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Shield, MapPin, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { api, fmtEUR } from "../lib/api";
import useSEO from "../hooks/useSEO";

export default function Landing() {
  const [vehicles, setVehicles] = useState([]);

  useSEO({
    title: "Caravan Reisen · Reisemobil mieten in Neusalza-Spremberg | Wohnmobilvermietung",
    description: "Reisemobile mieten bei Caravan Reisen in Neusalza-Spremberg. Kastenwagen, Alkoven & Teilintegrierte mit Live-Buchungskalender, Vollkasko-Versicherung und sicherer Online-Zahlung. Ab 75 €/Tag.",
    path: "/",
  });

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1781173772470-9fd318baf920?crop=entropy&cs=srgb&fm=jpg&w=2000&q=80"
          alt="Reisemobil in den Bergen"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-20 pt-40 w-full">
          <div className="max-w-3xl animate-fade-up">
            <span className="chip bg-white/20 border-white/30 text-white backdrop-blur-md">Reisemobilvermietung · Neusalza-Spremberg</span>
            <h1 className="text-white font-display font-bold text-5xl md:text-7xl mt-6 leading-[0.95] tracking-tight">
              Fahr dorthin,<br/>wo dein Herz ruft.
            </h1>
            <p className="text-white/85 text-lg md:text-xl mt-6 max-w-xl leading-relaxed">
              Miete ein voll ausgestattetes Reisemobil und mach jeden Tag zum Roadtrip. Faire Preise, moderne Fahrzeuge, deutsche Servicequalität.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/fahrzeuge" className="btn-primary" data-testid="hero-cta-book">
                Verfügbarkeit prüfen <ArrowRight size={18} />
              </Link>
              <a href="#wie" className="btn-outline text-white border-white/40 hover:bg-white/10">
                So funktioniert's
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* USP STRIP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-3 gap-8">
        {[
          { icon: Calendar, title: "Sofortige Buchung", text: "Live-Kalender mit tagesaktueller Verfügbarkeit." },
          { icon: Shield, title: "Rundum versichert", text: "Vollkasko und Pannenschutz sind bereits inklusive." },
          { icon: MapPin, title: "Standort Neusalza-Spremberg", text: "Abholung & Rückgabe Mo–Fr, 9:00–17:00 Uhr am Taubenheimer Weg 3." },
        ].map((item, i) => (
          <div key={i} className="p-8 bg-white rounded-2xl border border-[#D8D6D0] animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
            <item.icon size={28} className="text-[#C05C42]" />
            <h3 className="mt-6 font-display font-semibold text-xl">{item.title}</h3>
            <p className="mt-2 text-[#1B2A22]/70 text-sm leading-relaxed">{item.text}</p>
          </div>
        ))}
      </section>

      {/* FLEET PREVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="fleet-preview">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="chip">Unsere Flotte</span>
            <h2 className="section-title mt-4 max-w-xl">Reisemobile für jedes Abenteuer.</h2>
          </div>
          <Link to="/fahrzeuge" className="hidden md:inline-flex items-center gap-2 text-sm font-medium hover:text-[#C05C42]" data-testid="fleet-preview-all">
            Alle Fahrzeuge <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {vehicles.map((v) => (
            <Link key={v.id} to={`/fahrzeuge/${v.id}`} className="card-vehicle group" data-testid={`landing-vehicle-${v.id}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={v.images?.[0]} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-wider text-[#1B2A22]/60">{v.category}</div>
                <h3 className="mt-2 font-display font-semibold text-xl">{v.name}</h3>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-[#1B2A22]/60">{v.beds} Schlafplätze · {v.seats} Sitzplätze</span>
                  <span className="font-display font-bold text-lg">{fmtEUR(v.price_per_day)}<span className="text-xs font-normal text-[#1B2A22]/60">/Tag</span></span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="wie" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <img
            src="https://images.pexels.com/photos/9354562/pexels-photo-9354562.jpeg?auto=compress&w=1400"
            alt="Camping"
            className="rounded-2xl aspect-[4/5] object-cover"
          />
          <div>
            <span className="chip">Wie es funktioniert</span>
            <h2 className="section-title mt-4">In drei Schritten<br/>auf die Straße.</h2>
            <div className="mt-10 space-y-8">
              {[
                { n: "01", t: "Fahrzeug wählen", d: "Stöbere durch unsere Flotte und finde dein perfektes Reisemobil." },
                { n: "02", t: "Termin buchen", d: "Wähle deinen Zeitraum im Live-Kalender und ergänze passende Extras." },
                { n: "03", t: "Sicher bezahlen", d: "Zahle bequem online per Kreditkarte. Deine Buchung ist sofort bestätigt." },
              ].map((s) => (
                <div key={s.n} className="flex gap-6">
                  <div className="text-4xl font-display font-bold text-[#C05C42] tabular-nums">{s.n}</div>
                  <div>
                    <h4 className="font-display font-semibold text-lg">{s.t}</h4>
                    <p className="text-[#1B2A22]/70 mt-1 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <span className="chip">Bewertungen</span>
        <h2 className="section-title mt-4 max-w-2xl">Über 1.200 zufriedene Reisende können nicht irren.</h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {[
            { n: "Lena K.", t: "Traumhafter Trip an die Nordsee. Der Bergwind war blitzsauber und perfekt ausgestattet." },
            { n: "Markus P.", t: "Familienurlaub in Südtirol – der Alkoven bot allen Komfort. Sehr netter Service!" },
            { n: "Anja B.", t: "Buchung in 5 Minuten erledigt. Übergabe schnell und unkompliziert. Gerne wieder." },
          ].map((r, i) => (
            <div key={i} className="p-8 bg-white rounded-2xl border border-[#D8D6D0]">
              <div className="flex gap-1 text-[#C05C42]">
                {[...Array(5)].map((_, j) => <Star key={j} size={14} fill="currentColor" />)}
              </div>
              <p className="mt-4 text-[#1B2A22]/80 leading-relaxed">"{r.t}"</p>
              <div className="mt-4 text-sm font-semibold">{r.n}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="relative rounded-3xl overflow-hidden bg-[#1B2A22] text-white p-12 md:p-20">
          <div className="absolute inset-0 opacity-30">
            <img src="https://images.pexels.com/photos/19573235/pexels-photo-19573235.jpeg?auto=compress&w=1600" alt="" className="w-full h-full object-cover"/>
          </div>
          <div className="relative max-w-2xl">
            <h2 className="font-display font-bold text-4xl md:text-5xl leading-tight">Der nächste Sonnenaufgang wartet nicht.</h2>
            <p className="mt-4 text-white/70 text-lg">Sichere dir jetzt dein Reisemobil und starte in dein nächstes Abenteuer.</p>
            <Link to="/fahrzeuge" className="btn-primary mt-8" data-testid="footer-cta-book">
              Jetzt buchen <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
